import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
    MdHeadphones,
    MdDownload,
    MdSpeed,
    MdReplay5,
    MdForward5,
    MdErrorOutline,
    MdCheck,
    MdGraphicEq
} from 'react-icons/md';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import { getAegisAiDocumentMedia, getAegisAiDocumentById } from '../../Services/AegisAiDocumentsService';
import { downloadProviderFile } from '../../Services/ProviderService';
import '../../Styles/Modal.css';

const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 2];

export default function AiAudioPlayerModal({
    isOpen,
    onClose,
    title,
    fileName,
    fileSize,
    audioUrl,
    documentId,
    fileId,
    onDownload
}) {
    const { token } = useAuth();
    const audioRef = useRef(null);
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [playbackRate, setPlaybackRate] = useState(1);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloaded, setDownloaded] = useState(false);

    // Active playable audio URL: prioritize passed audioUrl (e.g. Azure Blob SAS URL), fallback to blobUrl
    const activeUrl = audioUrl || blobUrl;

    useEffect(() => {
        if (!isOpen) {
            setLoading(false);
            setError('');
            setIsPlaying(false);
            return;
        }

        // If direct stream URL already provided (e.g. Azure Blob SAS URL), use immediately
        if (audioUrl) {
            setBlobUrl(null);
            setLoading(false);
            setError('');
            return;
        }

        let isMounted = true;
        const abortController = new AbortController();

        const loadAudio = async () => {
            setLoading(true);
            setError('');
            try {
                // First attempt: check for fast streaming SAS URL via document details
                if (documentId) {
                    try {
                        const docRes = await getAegisAiDocumentById(documentId, token);
                        if (!isMounted) return;
                        if (docRes?.is_success && docRes?.data?.MediaStreamUrl) {
                            setBlobUrl(docRes.data.MediaStreamUrl);
                            setLoading(false);
                            return;
                        }
                    } catch (_) {}
                }

                // Fallback: download audio blob
                let blob = null;
                if (documentId) {
                    blob = await getAegisAiDocumentMedia(documentId, 'media', token, abortController.signal);
                } else if (fileId) {
                    const res = await downloadProviderFile(token, fileId);
                    blob = res?.blob;
                }

                if (!isMounted) return;

                if (blob) {
                    const url = URL.createObjectURL(blob);
                    setBlobUrl(url);
                    setLoading(false);
                } else {
                    setError('Audio recording could not be loaded from storage.');
                    setLoading(false);
                }
            } catch (err) {
                if (err.name === 'AbortError') return;
                console.error('Error loading audio:', err);
                if (isMounted) {
                    setError(err?.message || 'Failed to stream audio. Please try again.');
                    setLoading(false);
                }
            }
        };

        loadAudio();

        return () => {
            isMounted = false;
            abortController.abort();
        };
    }, [isOpen, audioUrl, documentId, fileId, token]);

    // Clean up created blob URL when unmounting
    useEffect(() => {
        return () => {
            if (blobUrl && blobUrl.startsWith('blob:')) {
                URL.revokeObjectURL(blobUrl);
            }
        };
    }, [blobUrl]);

    // Handle speed change
    const handleSpeedChange = (rate) => {
        setPlaybackRate(rate);
        if (audioRef.current) {
            audioRef.current.playbackRate = rate;
        }
    };

    // Seek +/- seconds
    const handleSeek = (delta) => {
        if (!audioRef.current) return;
        audioRef.current.currentTime = Math.max(0, Math.min(audioRef.current.duration || 0, audioRef.current.currentTime + delta));
    };

    // Download audio file
    const handleDownload = async () => {
        if (onDownload) {
            onDownload();
            return;
        }

        setIsDownloading(true);
        try {
            let downloadName = fileName || `${title || 'audio'}.m4a`;

            if (activeUrl) {
                const response = await fetch(activeUrl);
                const downloadBlob = await response.blob();
                const url = URL.createObjectURL(downloadBlob);
                const a = document.createElement('a');
                a.href = url;
                a.download = downloadName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                setTimeout(() => URL.revokeObjectURL(url), 2000);
                setDownloaded(true);
                setTimeout(() => setDownloaded(false), 2500);
            } else if (fileId) {
                const res = await downloadProviderFile(token, fileId);
                if (res?.blob) {
                    const url = URL.createObjectURL(res.blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = res.filename || downloadName;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    setTimeout(() => URL.revokeObjectURL(url), 2000);
                    setDownloaded(true);
                    setTimeout(() => setDownloaded(false), 2500);
                }
            }
        } catch (err) {
            console.error('Download error:', err);
            toast.error('Failed to download audio');
        } finally {
            setIsDownloading(false);
        }
    };

    // Keyboard shortcuts
    const handleKeyDown = useCallback((e) => {
        if (!isOpen) return;
        const target = e.target;
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
            return;
        }

        if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
        } else if (e.key === ' ' || e.key === 'k') {
            e.preventDefault();
            if (audioRef.current) {
                if (audioRef.current.paused) audioRef.current.play();
                else audioRef.current.pause();
            }
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            handleSeek(-5);
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            handleSeek(5);
        }
    }, [isOpen, onClose]);

    useEffect(() => {
        if (!isOpen) return;
        window.addEventListener('keydown', handleKeyDown);
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = originalOverflow;
        };
    }, [isOpen, handleKeyDown]);

    if (!isOpen) return null;

    const displayTitle = title || fileName || 'Audio Player';
    const ext = String(fileName || '').match(/\.([a-z0-9]+)$/i)?.[1]?.toUpperCase() || 'Audio';

    return createPortal(
        <div
            className="modal d-block hc-modal-backdrop hc-ai-video-modal-backdrop"
            tabIndex="-1"
            role="presentation"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="modal-dialog modal-dialog-centered modal-lg hc-ai-video-modal-dialog">
                <div
                    className="modal-content hc-modal-content hc-ai-video-modal-content border-0"
                    role="dialog"
                    aria-modal="true"
                    aria-label={`Preview audio: ${displayTitle}`}
                >
                    {/* Header */}
                    <div className="hc-modal-header d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-3 overflow-hidden flex-grow-1" style={{ minWidth: 0 }}>
                            <span className="hc-modal-icon flex-shrink-0" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)' }}>
                                <MdHeadphones size={22} />
                            </span>
                            <div className="hc-modal-header-text overflow-hidden flex-grow-1" style={{ minWidth: 0 }}>
                                <h5 className="modal-title fw-bold mb-0 text-truncate" title={displayTitle}>
                                    {displayTitle}
                                </h5>
                                <div className="text-muted small d-flex align-items-center gap-2 mt-1">
                                    <span className="badge bg-info-subtle text-info border border-info-subtle">
                                        {ext} Audio
                                    </span>
                                    {fileSize ? <span>• {fileSize}</span> : null}
                                    {fileName && fileName !== displayTitle ? (
                                        <span className="text-truncate opacity-75" title={fileName}>• {fileName}</span>
                                    ) : null}
                                </div>
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 flex-shrink-0">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1 fw-semibold"
                                onClick={handleDownload}
                                disabled={isDownloading}
                                title="Download audio file"
                            >
                                {isDownloading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                        <span>Downloading...</span>
                                    </>
                                ) : downloaded ? (
                                    <>
                                        <MdCheck size={16} color="#10b981" />
                                        <span style={{ color: '#10b981' }}>Saved</span>
                                    </>
                                ) : (
                                    <>
                                        <MdDownload size={16} />
                                        <span>Download</span>
                                    </>
                                )}
                            </button>

                            <button
                                type="button"
                                className="hc-modal-close"
                                onClick={onClose}
                                aria-label="Close"
                                title="Close (Esc)"
                            >
                                &times;
                            </button>
                        </div>
                    </div>

                    {/* Audio Player Showcase Stage */}
                    <div className="p-4" style={{ background: 'var(--hc-tint-1)' }}>
                        <div
                            className="p-4 rounded-4 position-relative overflow-hidden shadow-sm"
                            style={{
                                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                                border: '1px solid rgba(255, 255, 255, 0.08)'
                            }}
                        >
                            {/* Graphic Equalizer / Soundwave Visualizer */}
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <div className="d-flex align-items-center gap-3">
                                    <div
                                        className="d-flex align-items-center justify-content-center rounded-3 text-white"
                                        style={{ width: 48, height: 48, background: 'linear-gradient(135deg, #38bdf8 0%, #0284c7 100%)', boxShadow: '0 4px 14px rgba(2, 132, 199, 0.35)' }}
                                    >
                                        <MdGraphicEq size={26} />
                                    </div>
                                    <div>
                                        <div className="fw-bold text-white fs-6 text-truncate" style={{ maxWidth: 360 }} title={displayTitle}>
                                            {displayTitle}
                                        </div>
                                        <div className="text-white-50 small">
                                            {fileName || 'Audio Recording'}
                                        </div>
                                    </div>
                                </div>

                                {/* Animated sound bars */}
                                <div className="d-flex align-items-end gap-1" style={{ height: 32 }}>
                                    {[16, 24, 32, 20, 28, 14, 26, 30, 18, 22].map((h, i) => (
                                        <span
                                            key={i}
                                            style={{
                                                width: 4,
                                                height: isPlaying ? `${h}px` : '10px',
                                                background: '#38bdf8',
                                                borderRadius: 2,
                                                transition: 'height 0.25s ease',
                                                opacity: 0.85
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Player / Loading state */}
                            {loading ? (
                                <div className="d-flex align-items-center justify-content-center py-3 text-white gap-2">
                                    <span className="spinner-border spinner-border-sm text-info" role="status" aria-hidden="true" />
                                    <span className="small text-white-50">Connecting to secure audio storage...</span>
                                </div>
                            ) : error ? (
                                <div className="d-flex align-items-center justify-content-between py-2 text-danger small">
                                    <span className="d-inline-flex align-items-center gap-1">
                                        <MdErrorOutline size={18} />
                                        <span>{error}</span>
                                    </span>
                                    <button type="button" className="btn btn-outline-light btn-sm py-0 px-2" onClick={handleDownload}>
                                        Download Audio
                                    </button>
                                </div>
                            ) : activeUrl ? (
                                <audio
                                    ref={audioRef}
                                    src={activeUrl}
                                    controls
                                    autoPlay
                                    onPlay={() => setIsPlaying(true)}
                                    onPause={() => setIsPlaying(false)}
                                    className="w-100"
                                    style={{ borderRadius: 8, outline: 'none' }}
                                />
                            ) : null}
                        </div>
                    </div>

                    {/* Footer Controls */}
                    <div className="hc-modal-footer d-flex align-items-center justify-content-between p-3">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            {/* Speed Selector */}
                            <div className="d-inline-flex align-items-center gap-1 bg-white border rounded-2 px-2 py-1">
                                <span className="small text-muted d-inline-flex align-items-center gap-1 me-1">
                                    <MdSpeed size={14} />
                                    <span>Speed:</span>
                                </span>
                                {SPEED_OPTIONS.map((rate) => (
                                    <button
                                        key={rate}
                                        type="button"
                                        className={`btn btn-sm py-0 px-2 fw-semibold ${playbackRate === rate ? 'btn-primary' : 'btn-light text-muted'}`}
                                        style={{ fontSize: '11.5px' }}
                                        onClick={() => handleSpeedChange(rate)}
                                    >
                                        {rate}x
                                    </button>
                                ))}
                            </div>

                            {/* Seek Controls */}
                            <div className="btn-group btn-group-sm">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => handleSeek(-5)}
                                    title="Rewind 5s (←)"
                                >
                                    <MdReplay5 size={16} />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => handleSeek(5)}
                                    title="Forward 5s (→)"
                                >
                                    <MdForward5 size={16} />
                                </button>
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <span className="small text-muted d-none d-md-inline">
                                Space: Play/Pause • ← / →: Seek 5s • Esc: Close
                            </span>
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm px-3"
                                onClick={onClose}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
